import { getCore } from '../../../../../client/core/getCore';
import { getDefaultClient } from '../../../../../client/defaultClient';
import { redirectStateStorageKeySchema } from '../redirectStateStorageSchema';

type DetectOAuthRedirectParams = {
  url: URL;
};

/**
 * Detects if the current URL is an OAuth redirect from a social provider.
 *
 * This function examines the URL parameters to determine if it contains
 * OAuth callback data from a social authentication flow.
 *
 * @param params.url - The URL to check for OAuth redirect parameters.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to true if the URL is an OAuth redirect, false otherwise.
 */
export const detectOAuthRedirect = async (
  { url }: DetectOAuthRedirectParams,
  client = getDefaultClient()
): Promise<boolean> => {
  const core = getCore(client);

  const dynamicOauthState = url.searchParams.get('dynamicOauthState');
  const dynamicOauthCode = url.searchParams.get('dynamicOauthCode');

  if (!dynamicOauthState || !dynamicOauthCode) {
    return false;
  }

  // Wait for SDK to load
  await core.initTrack.waitForAll();

  const storedSocialRedirectState = await core.storage.getItem(
    redirectStateStorageKeySchema
  );

  if (
    !storedSocialRedirectState ||
    storedSocialRedirectState.state !== dynamicOauthState
  ) {
    return false;
  }

  return true;
};
